import React, { useState, useEffect } from "react";
import { Button, Column, Icon, Row, Sheet, Text } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";

type CameraDevice = {
  deviceId: string;
  label: string;
  groupId: string;
};

type CameraSelectionSheetProps = {
  open: boolean;
  onClose: () => void;
  onSelectCamera: (deviceId: string | null) => void;
};

const CameraSelectionSheet = ({
  open,
  onClose,
  onSelectCamera,
}: CameraSelectionSheetProps) => {
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      enumerateCameras();
    }
  }, [open]);

  const enumerateCameras = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Request camera permission first to get accurate device labels
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Get list of devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      // Stop the temporary stream
      stream.getTracks().forEach(track => track.stop());
      
      if (videoDevices.length === 0) {
        setError("No cameras found on this device");
        setLoading(false);
        return;
      }
      
      // Map devices to our type
      const cameraList: CameraDevice[] = videoDevices.map(device => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${videoDevices.indexOf(device) + 1}`,
        groupId: device.groupId,
      }));
      
      setCameras(cameraList);
      
      // Auto-select the first camera (usually the back/environment camera)
      if (cameraList.length > 0) {
        setSelectedDeviceId(cameraList[0].deviceId);
      }
      
      setLoading(false);
    } catch (err: any) {
      console.error("Failed to enumerate cameras:", err);
      setError(err?.message || "Failed to access cameras. Please grant camera permissions.");
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    onSelectCamera(selectedDeviceId);
  };

  return (
    <Sheet
      className="camera-selection-sheet"
      type="overlay"
      side="bottom"
      open={open}
      onClose={onClose}
    >
      <div style={{ borderRadius: "21px" }}>
        <Column spacing="400">
          <Row alignmentHorizontal="justify">
            <Text type="h400">Select Camera</Text>
            <Button onClick={onClose} type="icon">
              <Icon tokens={closeSmallTokens} />
            </Button>
          </Row>

          {loading && (
            <Column spacing="300" alignmentHorizontal="center" spacingInset="400">
              <Text type="b200" color="secondary">
                Detecting available cameras...
              </Text>
            </Column>
          )}

          {error && (
            <Column spacing="300">
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#FEF3F2",
                  borderRadius: "8px",
                  border: "1px solid #FEE4E2",
                }}
              >
                <Text type="b200" color="error">
                  {error}
                </Text>
              </div>
            </Column>
          )}

          {!loading && !error && cameras.length > 0 && (
            <>
              <Text type="b200">
                Choose which camera to use for this inspection:
              </Text>

              <Column spacing="300">
                {cameras.map((camera, index) => (
                  <div
                    key={camera.deviceId}
                    style={{
                      border: selectedDeviceId === camera.deviceId ? "2px solid #0073BB" : "1px solid #D5D5D5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: selectedDeviceId === camera.deviceId ? "#F0F8FF" : "white",
                      padding: "16px",
                    }}
                    onClick={() => setSelectedDeviceId(camera.deviceId)}
                  >
                    <Row alignmentVertical="center" spacing="300">
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          border: selectedDeviceId === camera.deviceId ? "6px solid #0073BB" : "2px solid #D5D5D5",
                          backgroundColor: "white",
                          flexShrink: 0,
                        }}
                      />
                      <Column spacing="100">
                        <Row spacing="200" alignmentVertical="center">
                          <Text type="b300">{camera.label || `Camera ${index + 1}`}</Text>
                        </Row>
                        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6c757d' }}>
                          <Text type="b100" color="secondary">
                            ID: {camera.deviceId.substring(0, 20)}...
                          </Text>
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6c757d' }}>
                          <Text type="b100" color="secondary">
                            Group: {camera.groupId.substring(0, 20)}...
                          </Text>
                        </div>
                      </Column>
                    </Row>
                  </div>
                ))}
              </Column>
            </>
          )}

          <Row
            alignmentHorizontal="justify"
            widths="fill"
            spacingInset="400 none none"
          >
            <Button onClick={onClose} type="tertiary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              type="primary"
              disabled={!selectedDeviceId || loading || !!error}
            >
              Start Camera
            </Button>
          </Row>
        </Column>
      </div>
    </Sheet>
  );
};

export default CameraSelectionSheet;